import type { TowerType } from "@mmotdg/shared";
import * as THREE from "three";
import { buildTowerModel, disposeTowerModel, type TowerModelView } from "./models/towers/index.js";

interface PreviewEntry {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  scene: THREE.Scene;
  view: TowerModelView;
  type: TowerType;
  skinId: string | undefined;
  locked: boolean;
  needsRender: boolean;
}

const LOCKED_ROTATION_Y = Math.PI * 0.18;

const PREVIEW_SIZE = 96;

class TowerPreviewPool {
  private renderer: THREE.WebGLRenderer | null = null;
  private camera: THREE.OrthographicCamera | null = null;
  private offscreen: HTMLCanvasElement | null = null;
  private entries = new Map<HTMLCanvasElement, PreviewEntry>();
  private rafId = 0;
  private size = PREVIEW_SIZE;
  private startedAt = 0;

  private ensureRenderer(): void {
    if (this.renderer) return;
    this.offscreen = document.createElement("canvas");
    this.offscreen.width = this.size;
    this.offscreen.height = this.size;
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.offscreen,
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: false,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(this.size, this.size, false);
    this.renderer.setClearColor(0x000000, 0);

    const view = 0.85;
    this.camera = new THREE.OrthographicCamera(-view, view, view, -view, 0.1, 20);
    this.camera.position.set(2.4, 2.6, 2.4);
    this.camera.lookAt(0, 0.55, 0);
    this.startedAt = performance.now();
  }

  attach(canvas: HTMLCanvasElement, type: TowerType, options?: { skinId?: string; locked?: boolean }): void {
    if (this.entries.has(canvas)) this.detach(canvas);
    this.ensureRenderer();
    canvas.width = this.size;
    canvas.height = this.size;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const scene = new THREE.Scene();
    scene.add(new THREE.AmbientLight(0xffffff, 0.65));
    const dir = new THREE.DirectionalLight(0xfff0d0, 1.1);
    dir.position.set(3, 5, 3);
    scene.add(dir);
    const hemi = new THREE.HemisphereLight(0x6ee7ff, 0x1c2333, 0.4);
    scene.add(hemi);

    const view = buildTowerModel(type, { level: 1, skinId: options?.skinId, withSelectionRing: false });
    view.group.position.set(0, 0, 0);
    scene.add(view.group);

    const locked = options?.locked === true;
    if (locked) view.group.rotation.y = LOCKED_ROTATION_Y;

    const entry: PreviewEntry = {
      canvas,
      ctx,
      scene,
      view,
      type,
      skinId: options?.skinId,
      locked,
      needsRender: true,
    };
    this.entries.set(canvas, entry);

    if (!this.rafId) this.start();
  }

  setLocked(canvas: HTMLCanvasElement, locked: boolean): void {
    const entry = this.entries.get(canvas);
    if (!entry || entry.locked === locked) return;
    entry.locked = locked;
    if (locked) entry.view.group.rotation.y = LOCKED_ROTATION_Y;
    entry.needsRender = true;
  }

  detach(canvas: HTMLCanvasElement): void {
    const entry = this.entries.get(canvas);
    if (!entry) return;
    entry.scene.remove(entry.view.group);
    disposeTowerModel(entry.view);
    this.entries.delete(canvas);
    if (this.entries.size === 0 && this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
  }

  private start(): void {
    const loop = () => {
      this.rafId = requestAnimationFrame(loop);
      if (!this.renderer || !this.camera || !this.offscreen) return;
      const t = (performance.now() - this.startedAt) * 0.0008;
      for (const entry of this.entries.values()) {
        if (entry.locked) {
          if (!entry.needsRender) continue;
          entry.needsRender = false;
        } else {
          entry.view.group.rotation.y = t;
        }
        this.renderer.render(entry.scene, this.camera);
        entry.ctx.clearRect(0, 0, this.size, this.size);
        entry.ctx.drawImage(this.offscreen, 0, 0);
      }
    };
    loop();
  }

  destroy(): void {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
    for (const entry of this.entries.values()) {
      entry.scene.remove(entry.view.group);
      disposeTowerModel(entry.view);
    }
    this.entries.clear();
    this.renderer?.dispose();
    this.renderer = null;
    this.camera = null;
    this.offscreen = null;
  }
}

export const towerPreviewPool = new TowerPreviewPool();
export { PREVIEW_SIZE };
